import { Form, Modal, Button, Spinner, Alert, Row } from "react-bootstrap";
import React, { useState, useEffect } from 'react';
import { getAuthToken } from "../../utils/AuthUtils";
import { API, Auth } from 'aws-amplify';
import DataGrid from "react-data-grid";
import "./styles.scss";

const formatSelectedCompanies = (compensationIds) => {
    const companies = [];
    if (!compensationIds || compensationIds.length === 0) {
        return companies;
    }

    for (const id of compensationIds) {
        const parts = id.split("#");
        const companyName = parts[0];
        const ticker = parts[1];
        const exchange = parts[5];
        let tickerWithExchange;
        if (exchange && exchange !== "NO_EXCHANGE") {
            tickerWithExchange = `${exchange}:${ticker}`;
        } else {
            tickerWithExchange = ticker;
        }
        companies.push({ companyName, tickerWithExchange });
    }
    return companies.sort((a, b) => a.companyName.localeCompare(b.companyName));
}

const ClientRequestModal = ({ show, onClose, requestType, compensationIds, surveyYear }) => {

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [selectedCompanies, setSelectedCompanies] = useState(formatSelectedCompanies(compensationIds));
    const [email, setEmail] = useState("");
    const [secondaryEmail, setSecondaryEmail] = useState("");
    const [sortColumns, setSortColumns] = useState([]);
    const [userNotes, setUserNotes] = useState("");

    const handleClose = () => {
        setEmail("")
        setLoading(false);
        setError(null);
        setSuccess(false);
        setSelectedCompanies([]);
        setSecondaryEmail("");
        setSortColumns([]);
        setUserNotes("");
        onClose();
    }

    const handleSubmit = async (e) => {
        setLoading(true);
        setError(null);

        const requestBody = {
            requestType,
            companies: selectedCompanies,
            email,
            secondaryEmail,
            surveyYear,
            userNotes
        };

        const authToken = await getAuthToken();
        API.post('AnnualCompensations', '/client-requests/survey-data', {
            headers: {
                'Token': authToken
            },
            body: requestBody
        }).then((response) => {
            setSuccess(true);
            setLoading(false);
        }).catch((error) => {
            setLoading(false);
            setSuccess(false);
            if (error !== undefined && error.response !== undefined &&
                error.response.data !== undefined && error.response.data.message !== undefined) {
                setError(error.response.data.message);
            } else {
                setError("An error occurred while submitting your request. Please try again later.");
            }
        })
    }

    const columns = [
        {
            key: "companyName",
            name: 'Company Name',
            sortable: true,
            filterable: true,
            frozen: true,
        },
        {
            key: "tickerWithExchange",
            name: 'Ticker',
            sortable: true,
            filterable: true,
            frozen: true,
        }
    ]


    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            async function getEmail() {
                const user = await Auth.currentAuthenticatedUser();
                setEmail(user?.attributes?.email);
            }
            getEmail();
            setLoading(false);
            setError(null);
            setSuccess(false);
            setSelectedCompanies(formatSelectedCompanies(compensationIds));
            setSecondaryEmail("");
        }
        return () => { isMounted = false };
    }, [compensationIds, show]);

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" dialogClassName="dashboard-modal">
            <Modal.Header closeButton>
                <Modal.Title>{requestType === "CUSTOM_CUT" ? "Request Customized Output" : "Request Regression Data"}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); handleSubmit(event); }}>
                <Modal.Body>
                    {error?.length > 0 && <Alert variant="danger" dismissible>{error}</Alert>}
                    {success && <Alert variant="success" dismissible>Your request has been submitted successfully!</Alert>}
                    <p>You have selected {selectedCompanies.length} companies. Our team will review your request and email you the results.</p>
                    <div className="selected-companies-grid">
                        <DataGrid
                            className={'grid rdg-light filter-container'}
                            columns={columns}
                            rows={selectedCompanies}
                            sortColumn={sortColumns}
                            onSortColumnsChange={setSortColumns}
                        />
                    </div>
                    <div className="recipient-data">
                        <Form.Group className="mb-3" controlId="formPrimaryEmail">
                            <Form.Label>Primary Recipient Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter primary recipient email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formSecondaryEmail">
                            <Form.Label>Secondary Recipient Email (optional)</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter secondary recipient email"
                                value={secondaryEmail}
                                onChange={(e) => setSecondaryEmail(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="userNotes">
                            <Form.Label>Notes (Required)</Form.Label>
                            <Form.Control
                                as="textarea"
                                maxLength="250"
                                placeholder="Please enter any notes"
                                value={userNotes}
                                onChange={(e) => setUserNotes(e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Please let us know what you're looking for in a custom output. (e.g. roles, subset, or breakout)
                            </Form.Text>
                        </Form.Group>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Row>
                        <p className="text-muted" style={{ fontSize: 14 }}>For questions about the survey data or regression analysis, please contact our support team at <a href="mailto:data@fwcook.com">data@fwcook.com</a></p>
                    </Row>
                    {loading ?
                        <Button variant="btn btn-primary"
                            disabled={true}>
                            <output>Submitting...</output>
                            <Spinner
                                as="span"
                                animation="grow"
                                size="sm"
                                aria-hidden="true"
                            />
                        </Button> :
                        <Button variant="btn btn-primary"
                            disabled={userNotes.length < 5}
                            type="submit"
                            style={{ "width": "25%", "borderRadius": "2px" }}>
                            Submit Request
                        </Button>}
                    <Button variant="btn btn-secondary" onClick={handleClose} style={{ "width": "20%", "borderRadius": "2px" }} disabled={loading}>
                        Close
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    )
}


export default ClientRequestModal;
